import type {SxProps, Theme} from '@mui/material';

/**
 * FS mode table row density — match adjustment mode Typography rows (~20.01px).
 * Cell py 0.5px; level-2 leaf py 5px.
 * MoneyField Outlined must be crushed to Typography height (default size=small ≈ 34px).
 */
export const FS_COMPACT_ROW_H = 20;
export const FS_COMPACT_CELL_PY = '0.5px';
export const FS_LEVEL2_CELL_PY = '5px';
/** Virtual estimate: 20 + (5 − 0.5) × 2 */
export const FS_LEVEL2_ROW_H = 29;

/** TableHead / default body cell padding — same as adjustment TableRow. */
export const fsCompactCellPadSx = {
    paddingTop: FS_COMPACT_CELL_PY,
    paddingBottom: FS_COMPACT_CELL_PY,
} as const;

/**
 * MoneyField chrome for FS amount cells.
 * Force OutlinedInput to ~14px×1.43 line box (same as adjustment Typography body2 @ 14).
 */
export const fsCompactMoneyFieldSx = {
    width: '100%',
    fontSize: 14,
    lineHeight: 1.43,
    m: 0,
    '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
        width: '100%',
        minHeight: '0 !important',
        height: 'auto',
        padding: '0 !important',
        boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
        textAlign: 'center' as const,
        fontSize: '14px !important',
        lineHeight: '1.43 !important',
        paddingLeft: '0.8px',
        paddingRight: '0.8px',
        paddingTop: '0 !important',
        paddingBottom: '0 !important',
        height: 'auto',
        boxSizing: 'border-box',
    },
} as const;

/** Expand / COT IconButton — default MUI size=small is 34px and blows FS row height. */
export const fsCompactIconButtonSx = {
    p: 0,
    width: 18,
    height: 18,
    minWidth: 18,
    minHeight: 18,
    '& .MuiSvgIcon-root': {fontSize: 18},
} as const;

/** Name column Typography — same metrics as adjustment amount typography. */
export const fsCompactNameSx = {
    fontSize: 14,
    lineHeight: 1.43,
} as const;

/**
 * Item-name InputField (adjustment InlineEditableName / PL FS custom rename).
 * Same crushed Outlined height as FS MoneyField — edit must not grow the row.
 */
export const fsCompactNameInputSx = {
    ...fsCompactMoneyFieldSx,
    minWidth: 0,
    '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
        ...fsCompactMoneyFieldSx['& .MuiOutlinedInput-input, & .MuiInputBase-input'],
        textAlign: 'left' as const,
        paddingLeft: '4px',
        paddingRight: '4px',
    },
} as const;

/**
 * Per-cell Last Year vertical rule (non-virtual tables: BS / PL adjustment).
 * Paint-only — avoids border box-model shifts.
 */
export const lastYearColumnSeparatorSx: SxProps<Theme> = {
    boxShadow: (theme) => `inset 2px 0 0 ${theme.palette.divider}`,
};

/**
 * Adjust / Adjust Total header when columns can collapse.
 * Hover must stay opaque: MUI `action.hover` is rgba and lets stickyHeader
 * show body amounts + cell borders through the th.
 */
export const adjustColumnToggleHeaderSx = {
    cursor: 'pointer' as const,
    backgroundColor: 'background.paper',
    '&:hover': {
        backgroundColor: (theme: Theme) =>
            theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
    },
};

/**
 * Table-level Last Year rule for virtualized PL FS.
 * One selector instead of sx on every mounted cell (cheaper under windowing).
 * Targets 2nd-last column (Last Year sits before Change).
 */
export const lastYearColumnSeparatorOnTableSx: SxProps<Theme> = {
    '& thead .MuiTableCell-root:nth-last-child(2), & tbody .MuiTableCell-root:nth-last-child(2)': {
        boxShadow: (theme) => `inset 2px 0 0 ${theme.palette.divider}`,
    },
};

/**
 * FS mode Current / YOA / Last Year cell content wrapper.
 * TableCell align=center does not center block flex children (e.g. COT + MoneyField).
 * Children stay content-sized (no flex-grow) so MULTI YOA + trailing icon stay centered as a group.
 */
export const fsAmountCellContentSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: 0,
    gap: 0.5,
    boxSizing: 'border-box',
    '& > *': {
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: '100%',
    },
    // Trailing COT / action controls must not eat the amount column
    '& > .MuiIconButton-root': {
        flex: '0 0 auto',
    },
} as const;
